import { useLocale } from '@tundr/i18n';
import { useModal } from '@tundr/modal';
import { useNavigationGuardStore } from '@tundr/routing';
import { defineStore } from 'pinia';
import { type Component, computed, shallowRef } from 'vue';
import type { SlideoverConfig } from '../components/Slideover';

export interface SlideoverStoreProps {
  component: null | Component;
  props?: Record<string, unknown>;
  config?: SlideoverConfig;
  events?: {
    onClose?: () => void;
    onConfirm?: () => void;
    onCancel?: () => void;
  };
  labels?: {
    confirm?: string;
    cancel?: string;
    confirmTestId?: string;
    cancelTestId?: string;
  };
}

export interface CloseSlideoverOptions {
  skipGuardConfirm?: boolean;
}

const initialState = {
  component: null,
  props: {},
  config: { blocking: false, firstSelectionOnLastItem: true },
};

export const useSlideoverStore = defineStore('slideover', () => {
  const slideoverState = shallowRef<SlideoverStoreProps>(initialState);

  const { openConfirmModal } = useModal();
  const navigationGuardStore = useNavigationGuardStore();
  const { t } = useLocale('common');
  const navigationIsBlocked = computed(() => navigationGuardStore.isBlocked);

  const openSlideover = ({
    component,
    props = {},
    config = { blocking: false, firstSelectionOnLastItem: true },
    events = {},
    labels = {},
  }: SlideoverStoreProps) => {
    slideoverState.value = {
      component,
      props,
      config,
      events,
      labels,
    };
  };

  const closeSlideover = (options?: CloseSlideoverOptions) => {
    if (!isSlideoverOpen.value) {
      return;
    }
    const skipGuardConfirm =
      options?.skipGuardConfirm === true ||
      slideoverState.value.config?.skipGuardConfirm === true;
    if (navigationIsBlocked.value && !skipGuardConfirm) {
      openConfirmModal({
        title: t('confirm_close.title'),
        message: t('confirm_close.description'),
        config: { blocking: true },
        labels: {
          confirm: t('actions.close'),
          cancel: t('actions.cancel'),
        },
        events: {
          onConfirm: () => {
            slideoverState.value.events?.onClose?.();
            slideoverState.value = initialState;
          },
        },
      });
      return;
    }
    if (slideoverState.value.events?.onClose) {
      slideoverState.value.events.onClose();
    }
    slideoverState.value = initialState;
  };

  const isSlideoverOpen = computed<boolean>(
    () => !!slideoverState.value.component,
  );

  return { slideoverState, openSlideover, closeSlideover, isSlideoverOpen };
});
